// Shared entity types, ported from the field lists documented in the
// WOOF-01 rebuild plan (docs/ARCHITECTURE.md). These are DTO shapes exchanged
// over REST between services and the frontend - not DB row types (each
// service's DB layer maps its own snake_case columns to/from these).

export interface Site {
  code: string;
  name: string;
  country: string;
  active: boolean;
}

export interface User {
  id: string;
  username: string;
  mustChangePassword: boolean;
  firstLogin: boolean;
  role: string;
  sites: string[];
  studyIds: number[];
  fullName: string;
  email: string;
  active: boolean;
  createdAt: string;
  createdBy: string | null;
  lastLogin: string | null;
  passwordChangedAt: string | null;
  deactivatedAt: string | null;
  deactivatedReason: string | null;
  passwordResetAt: string | null;
}

export type PatientStatus = "SCREENING" | "ACTIVE" | "COMPLETED" | "WITHDRAWAL BY SUBJECT" | "SCREEN FAILURE";
export type ReprodStatus = "intact" | "neutered" | "unknown";

export interface Patient {
  id: string;
  siteid: string;
  sequence: number;
  initials: string;
  breed: string | null;
  brthdtc: string | null;
  sex: "M" | "F" | "U" | "UNDIFFERENTIATED" | null;
  reprodStatus: ReprodStatus | null;
  tumorType: "melanoma" | "oscc" | "other";
  notes: string | null;
  status: PatientStatus;
  statusUpdatedAt: string | null;
  statusReason: string | null;
  createdAt: string;
  createdBy: string;
  studyId: number | null;
  species: "animal" | "human";
}

// A study spans one or more investigation sites and contains many patients
// (Patient.studyId, one-to-many) - a patient belongs to at most one study.
// A study's species is fixed for all its patients: an animal study cannot
// contain human patients and vice versa (enforced in studyRepo/patientRepo).
export interface Study {
  id: number;
  name: string;
  description: string | null;
  siteCodes: string[];
  patientCount: number;
  createdAt: string;
  createdBy: string;
  species: "animal" | "human";
}

export interface PatientStatusHistoryEntry {
  status: PatientStatus;
  reason: string | null;
  changedAt: string;
  changedBy: string;
}

export interface Visit {
  id: number;
  patientId: string;
  siteCode: string;
  visitKey: string;
  visitDate: string | null;
  isExtra: boolean;
  createdAt: string;
  createdBy: string;
}

export interface VisitSection {
  visitId: number;
  sectionId: string;
  data: Record<string, unknown>;
  updatedAt: string;
  updatedBy: string;
}

export interface MedicalHistoryEntry {
  id: string;
  patientId: string;
  mhterm: string;
  mhstdat: string | null;
  mhendat: string | null;
  ongoing: boolean;
  treatment: string | null;
  createdAt: string;
  createdBy: string;
}

export interface ConcomitantMedEntry {
  id: string;
  patientId: string;
  cmtrt: string;
  cmdose: string | null;
  cmroute: string;
  cmindc: string | null;
  cmstdat: string | null;
  cmendat: string | null;
  ongoing: boolean;
  linkedTo: string | null;
  linkedVisit: string | null;
  atcd: boolean;
  source: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface ExtraAdminEntry {
  id: string;
  patientId: string;
  exstdat: string;
  adminTime: string | null;
  exdose: number | null;
  exloc: string | null;
  operator: string | null;
  linkedVisit: string | null;
  notes: string | null;
  tumorVolumeRef: number | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface SaeDetails {
  criteria: string[];
  awareDate: string;
  chronology: string;
  management: string | null;
  reporter: string | null;
  pvNotifiedDate: string | null;
}

export interface AdverseEvent {
  id: string;
  patientId: string;
  siteCode: string;
  aeterm: string;
  aedecod: string | null;
  aetoxgr: number;
  type: string;
  aerel: string;
  aeout: string;
  aeacn: string;
  aestdat: string;
  aeendat: string | null;
  aeser: boolean;
  ongoing: boolean;
  visitKey: string | null;
  sae: SaeDetails | null;
  source: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type DeviationType = "CRITICAL" | "MAJOR" | "MINOR" | "PLANNED";
export type DeviationStatus = "Ouverte" | "En cours" | "Résolue" | "Fermée";

export interface Deviation {
  id: string;
  pdid: string;
  patientId: string | null;
  siteCode: string;
  studyId: number | null;
  dvcat: DeviationType;
  dvterm: string;
  description: string | null;
  section: string | null;
  protocolRef: string | null;
  dvstdat: string | null;
  date: string | null;
  action: string | null;
  assignedTo: string | null;
  status: DeviationStatus;
  resolvedDate: string | null;
  reportedToSponsor: boolean;
  sponsorReportDate: string | null;
  plannedClosure: string | null;
  relatedQueryId: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type DeviationActionKind = "corrective" | "preventive";

export interface DeviationAction {
  id: string;
  deviationId: string;
  kind: DeviationActionKind;
  description: string;
  dueDate: string | null;
  deliverableFilename: string | null;
  deliverableSize: number | null;
  deliverableContentType: string | null;
  deliverableUploadedAt: string | null;
  deliverableUploadedBy: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type QueryStatus = "Ouverte" | "Répondue" | "Fermée";

export interface QueryHistoryEntry {
  ts: string;
  user: string;
  action: string;
  comment: string | null;
}

export interface QueryEntity {
  id: string;
  qid: string;
  patientId: string;
  siteCode: string;
  studyId: number | null;
  visitKey: string | null;
  field: string | null;
  question: string;
  answer: string | null;
  status: QueryStatus;
  assignedTo: string | null;
  severity: string | null;
  history: QueryHistoryEntry[];
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  user: string;
  userRole: string;
  sessionId: string | null;
  action: string;
  scope: string;
  target: string | null;
  details: string | null;
  oldValue: unknown;
  newValue: unknown;
}
